import type { CommonParams } from "src/queries/useCommonQueryParams";

/** Simple URL builder: (p) => `/api/path/${p.param}` */
export type SimpleUrlBuilder = (
  params: CommonParams & Record<string, unknown>
) => string;

/**
 * Builds URL from params, reporting which params were read (for body exclusion), which of
 * those resolved empty, and whether the URL is safe to request (isUrlValid).
 *
 * An empty param only matters where it lands:
 *  - **path** — `/api/envs/demo/apps//runs` addresses a different route. Never request it.
 *  - **query string** — `?app_name=` is just an omitted filter. Fine.
 *  - **read but not interpolated** — a conditional builder (`p.x ? urlA : urlB`) reads a
 *    param without putting it in the URL. Fine. Empty params read as `""` so that such a
 *    builder takes the branch it would take for a genuinely absent value.
 *
 * The `//` / trailing-`/` test is the ESCAPE HATCH for the last two cases, not the primary
 * check: when nothing is empty the URL is valid whatever its shape. #4585 dropped the
 * `emptyParams.length === 0` half and left the hatch as the whole test, which then rejected
 * any URL ending in `/` even with every param present — silently disabling the
 * pipeline-settings query for six months (#6620), with no request, no error and no log line.
 */
export const buildQueryUrl = (
  urlBuilder: SimpleUrlBuilder,
  params: CommonParams
) => {
  const urlParams: string[] = [];
  const emptyParams: string[] = [];

  const proxy = new Proxy(params, {
    get(target, prop: string) {
      urlParams.push(prop);
      const value = target[prop as keyof CommonParams];
      if (value == null || value === "") {
        emptyParams.push(prop);
        return "";
      }
      return encodeURIComponent(String(value));
    },
  });

  const url = urlBuilder(proxy);
  const isUrlValid =
    emptyParams.length === 0 || (!url.includes("//") && !url.endsWith("/"));

  return { url, urlParams, emptyParams, isUrlValid };
};
